import { useState, useEffect, useCallback } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Search, ArrowRight, Package, X, ChevronRight, ChevronLeft, Upload, Link2, Unlink } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api/buildings`;

export default function AccountingItems() {
  const [items, setItems] = useState([]);
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  const [linkedCount, setLinkedCount] = useState(0);
  const [totalAll, setTotalAll] = useState(0);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [group, setGroup] = useState('');
  const [linked, setLinked] = useState('all');
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [uploading, setUploading] = useState(false);
  const [linkModal, setLinkModal] = useState(null); // { item }

  const fetchGroups = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/accounting-items/groups`);
      setGroups(res.data?.groups || []);
    } catch { /* ignore */ }
  }, []);

  const fetchItems = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: pageSize, linked };
      if (search.trim()) params.search = search.trim();
      if (group) params.group = group;
      const res = await axios.get(`${API}/accounting-items`, { params });
      setItems(res.data?.items || []);
      setTotal(res.data?.total || 0);
      setLinkedCount(res.data?.linked_count || 0);
      setTotalAll(res.data?.total_all || 0);
    } catch (e) {
      toast.error('فشل في جلب الأصناف');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, search, group, linked]);

  useEffect(() => { fetchGroups(); }, [fetchGroups]);
  useEffect(() => { fetchItems(); }, [fetchItems]);
  useEffect(() => { setPage(1); }, [search, group, linked]);

  const handleUnlink = async (itemNumber) => {
    if (!window.confirm('إلغاء الربط لهذا الصنف؟')) return;
    try {
      await axios.delete(`${API}/accounting-items/${itemNumber}/link`);
      toast.success('تم إلغاء الربط');
      fetchItems();
    } catch {
      toast.error('فشل الإلغاء');
    }
  };

  const handleImport = async (file) => {
    if (!file) return;
    const formData = new FormData();
    formData.append('file', file);
    try {
      setUploading(true);
      const res = await axios.post(`${API}/accounting-items/import-excel`, formData, {
        headers: { 'Content-Type': 'multipart/form-data' }
      });
      toast.success(res.data?.message || 'تم الاستيراد');
      fetchGroups();
      fetchItems();
    } catch {
      toast.error('فشل الاستيراد');
    } finally {
      setUploading(false);
    }
  };

  const totalPages = Math.max(1, Math.ceil(total / pageSize));

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50 p-4 md:p-6" dir="rtl">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <Link to="/buildings" className="inline-flex items-center gap-2 text-gray-700 hover:text-gray-900">
            <ArrowRight className="w-5 h-5" />
            العودة
          </Link>
          <div className="flex items-center gap-2">
            <label className="cursor-pointer">
              <input type="file" accept=".xlsx,.xls" className="hidden" onChange={(e) => handleImport(e.target.files?.[0])} data-testid="import-acc-items-input" />
              <span className="inline-flex items-center gap-2 px-4 py-2 border-2 border-green-600 text-green-700 rounded-lg hover:bg-green-50 text-sm font-semibold">
                <Upload className="w-4 h-4" />
                {uploading ? 'جاري الاستيراد...' : 'استيراد Excel'}
              </span>
            </label>
          </div>
        </div>

        <div className="flex items-center gap-3 mb-6">
          <div className="p-3 bg-blue-600 rounded-2xl">
            <Package className="w-7 h-7 text-white" />
          </div>
          <div>
            <h1 className="text-2xl md:text-3xl font-bold text-gray-900">أصناف البرنامج المحاسبي</h1>
            <p className="text-sm text-gray-500">
              إجمالي: <span className="font-bold text-blue-700">{total.toLocaleString('ar-SA')}</span> صنف
              {totalAll > 0 && (
                <span className="mr-3">
                  • مربوط: <span className="font-bold text-emerald-700">{linkedCount.toLocaleString('ar-SA')}</span>
                  <span className="text-gray-400"> ({Math.round((linkedCount / totalAll) * 100)}%)</span>
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Link filter tabs */}
        <div className="flex items-center gap-2 mb-3">
          {[
            { id: 'all', label: 'الكل', color: 'bg-gray-700' },
            { id: 'yes', label: 'مربوطة', color: 'bg-emerald-600' },
            { id: 'no', label: 'غير مربوطة', color: 'bg-red-600' },
          ].map(t => (
            <button
              key={t.id}
              onClick={() => setLinked(t.id)}
              data-testid={`link-filter-${t.id}`}
              className={`px-4 py-1.5 rounded-lg text-sm font-semibold transition ${
                linked === t.id ? `${t.color} text-white shadow` : 'bg-white text-gray-700 border hover:bg-gray-50'
              }`}
            >
              {t.label}
            </button>
          ))}
        </div>

        {/* Filters */}
        <Card className="mb-4">
          <CardContent className="p-4">
            <div className="grid grid-cols-1 md:grid-cols-12 gap-3">
              <div className="md:col-span-6 relative">
                <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
                <input
                  type="text"
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  placeholder="بحث برقم الصنف أو الاسم..."
                  data-testid="acc-search-input"
                  className="w-full pr-9 pl-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
                />
              </div>
              <div className="md:col-span-5">
                <select
                  value={group}
                  onChange={(e) => setGroup(e.target.value)}
                  data-testid="acc-group-select"
                  className="w-full h-10 px-3 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none bg-white"
                >
                  <option value="">كل المجموعات ({groups.length})</option>
                  {groups.map((g, i) => (
                    <option key={i} value={g.group_name}>
                      {g.group_number ? `${g.group_number} - ` : ''}{g.group_name} ({g.count})
                    </option>
                  ))}
                </select>
              </div>
              <div className="md:col-span-1">
                {(search || group) && (
                  <Button
                    variant="outline"
                    onClick={() => { setSearch(''); setGroup(''); }}
                    className="w-full h-10"
                    data-testid="acc-clear-filters"
                  >
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0 overflow-x-auto">
            {loading ? (
              <div className="py-16 text-center text-gray-400">جاري التحميل...</div>
            ) : items.length === 0 ? (
              <div className="py-16 text-center text-gray-400">
                <Package className="w-14 h-14 mx-auto mb-3 opacity-40" />
                <p className="text-sm">لا توجد أصناف مطابقة</p>
              </div>
            ) : (
              <table className="w-full text-sm">
                <thead className="bg-gray-50">
                  <tr>
                    <th className="px-3 py-3 text-right text-xs font-bold text-gray-600 w-16">#</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-gray-600 w-32">رقم الصنف</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-gray-600">اسم الصنف</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-gray-600 w-36">المجموعة</th>
                    <th className="px-3 py-3 text-right text-xs font-bold text-gray-600 w-64">الربط بالمادة الأساسية</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((it, i) => (
                    <tr key={`${it.item_number}-${i}`} className="border-t hover:bg-blue-50/40" data-testid={`acc-item-${i}`}>
                      <td className="px-3 py-2 text-gray-500 text-xs">{(page - 1) * pageSize + i + 1}</td>
                      <td className="px-3 py-2 font-mono font-semibold text-blue-700">{it.item_number}</td>
                      <td className="px-3 py-2 text-gray-800">{it.item_name}</td>
                      <td className="px-3 py-2 text-xs">
                        <span className="inline-block px-2 py-0.5 rounded bg-gray-100 text-gray-700">{it.group_name || '-'}</span>
                      </td>
                      <td className="px-3 py-2 text-xs">
                        {it.linked_material_code ? (
                          <div className="flex items-center gap-2">
                            <div className="flex-1 min-w-0">
                              <div className="font-bold text-emerald-700 font-mono text-xs">{it.linked_material_code}</div>
                              <div className="text-gray-600 truncate text-[11px]" title={it.linked_material_name}>{it.linked_material_name}</div>
                            </div>
                            <div className="flex items-center gap-1">
                              <button
                                onClick={() => setLinkModal({ item: it })}
                                data-testid={`edit-link-${i}`}
                                className="p-1 text-blue-600 hover:bg-blue-50 rounded"
                                title="تعديل الربط"
                              >
                                <Link2 className="w-3.5 h-3.5" />
                              </button>
                              <button
                                onClick={() => handleUnlink(it.item_number)}
                                data-testid={`unlink-${i}`}
                                className="p-1 text-red-600 hover:bg-red-50 rounded"
                                title="إلغاء الربط"
                              >
                                <Unlink className="w-3.5 h-3.5" />
                              </button>
                            </div>
                          </div>
                        ) : (
                          <button
                            onClick={() => setLinkModal({ item: it })}
                            data-testid={`link-btn-${i}`}
                            className="inline-flex items-center gap-1 px-2 py-1 border-2 border-dashed border-emerald-400 text-emerald-700 rounded hover:bg-emerald-50 text-xs font-semibold"
                          >
                            <Link2 className="w-3.5 h-3.5" />
                            ربط
                          </button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </CardContent>
        </Card>

        {/* Pagination */}
        {total > 0 && (
          <div className="flex items-center justify-between mt-4 bg-white rounded-lg px-3 py-2 border" data-testid="acc-pagination">
            <div className="text-sm text-gray-600">
              عرض <span className="font-bold text-gray-900">{(page - 1) * pageSize + 1}</span> - <span className="font-bold text-gray-900">{Math.min(page * pageSize, total)}</span> من <span className="font-bold text-blue-700">{total.toLocaleString('ar-SA')}</span>
            </div>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                data-testid="acc-page-prev"
                className="h-8 w-8 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
              <span className="px-3 h-8 flex items-center gap-1.5 text-sm">
                صفحة <span className="font-bold">{page}</span> من {totalPages}
              </span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                data-testid="acc-page-next"
                className="h-8 w-8 flex items-center justify-center rounded border hover:bg-gray-50 disabled:opacity-40"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Link Modal */}
      {linkModal && (
        <LinkMaterialModal
          accountingItem={linkModal.item}
          onClose={() => setLinkModal(null)}
          onSaved={() => { setLinkModal(null); fetchItems(); }}
        />
      )}
    </div>
  );
}

function LinkMaterialModal({ accountingItem, onClose, onSaved }) {
  const [materials, setMaterials] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [q, setQ] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/materials`);
        setMaterials(res.data || []);
      } catch { /* ignore */ } finally { setLoading(false); }
    })();
  }, []);

  const filtered = materials.filter(m => {
    const s = q.trim().toLowerCase();
    if (!s) return true;
    return (m.code || '').toLowerCase().includes(s) || (m.name || '').toLowerCase().includes(s);
  }).slice(0, 100);

  const handleSave = async () => {
    if (!selected) {
      toast.error('اختر مادة أولاً');
      return;
    }
    try {
      setSaving(true);
      const res = await axios.put(
        `${API}/accounting-items/${accountingItem.item_number}/link`,
        { material_code: selected.code }
      );
      toast.success(res.data?.message || 'تم الربط');
      onSaved();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'فشل الربط');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[85vh] overflow-hidden flex flex-col" onClick={e => e.stopPropagation()} data-testid="link-modal">
        <div className="p-4 border-b bg-gradient-to-l from-blue-50 to-white">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-lg font-bold text-gray-800">ربط الصنف بمادة أساسية</h2>
              <p className="text-xs text-gray-500 mt-0.5">
                <span className="font-mono text-blue-700">{accountingItem.item_number}</span> — {accountingItem.item_name}
              </p>
            </div>
            <button onClick={onClose} className="p-2 hover:bg-gray-100 rounded-lg" data-testid="close-link-modal">
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>
        <div className="p-3 border-b bg-gray-50">
          <div className="relative">
            <Search className="w-4 h-4 absolute right-3 top-1/2 -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              value={q}
              onChange={e => setQ(e.target.value)}
              placeholder="بحث بكود أو اسم المادة..."
              data-testid="link-search-input"
              autoFocus
              className="w-full pr-9 pl-3 py-2 border rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:outline-none"
            />
          </div>
        </div>
        <div className="flex-1 overflow-auto p-2">
          {loading ? (
            <div className="py-8 text-center text-gray-400">جاري التحميل...</div>
          ) : filtered.length === 0 ? (
            <div className="py-8 text-center text-gray-400">لا توجد نتائج</div>
          ) : (
            <div className="space-y-1">
              {filtered.map((m) => (
                <button
                  key={m.code}
                  type="button"
                  onClick={() => setSelected(m)}
                  data-testid={`mat-option-${m.code}`}
                  className={`w-full text-right p-2.5 rounded-lg border transition ${
                    selected?.code === m.code ? 'border-blue-500 bg-blue-50 shadow-sm' : 'border-gray-200 hover:bg-gray-50'
                  }`}
                >
                  <div className="flex items-center justify-between">
                    <div className="min-w-0">
                      <span className="font-mono font-bold text-blue-700 text-sm">{m.code}</span>
                      <span className="mx-2 text-gray-400">•</span>
                      <span className="text-gray-800 text-sm">{m.name}</span>
                    </div>
                    <span className="text-xs text-gray-500">{m.unit}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>
        <div className="p-3 border-t bg-gray-50 flex items-center justify-end gap-2">
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button
            onClick={handleSave}
            disabled={!selected || saving}
            data-testid="save-link-btn"
            className="bg-blue-600 hover:bg-blue-700"
          >
            {saving ? 'جاري الحفظ...' : selected ? `ربط بـ ${selected.name}` : 'اختر مادة'}
          </Button>
        </div>
      </div>
    </div>
  );
}
